import { type ComponentPropsWithRef, useId, useMemo } from "react";
import { useFormikContext } from "formik";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { useLockDate } from "@api/lock.api";
import { ScheduleTemplateResponse } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { Entity } from "@common/types/entity";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { NumberFormat } from "@design-system/NumberFormat/NumberFormat";
import { VStack } from "@design-system/VStack/VStack";
import { usePortalContainer } from "@design-system/overlay/OverlayContainerContext";
import { Input } from "@design-system/Input/Input";
import { getFirstUnlockedDateOnly } from "@helpers/lock";
import {
    ApplySourceType,
    ScheduleApplyFormValues,
} from "./scheduleApplyFormHelpers";
import { ScheduleApplyInvoiceField } from "./ScheduleApplyInvoiceField";
import styles from "./ScheduleApplyModal.module.scss";

export type { ApplySourceType };

const fields = createFormikFields<ScheduleApplyFormValues>();

const ApplyFieldInput = (
    props: Omit<ComponentPropsWithRef<"input">, "size">,
) => <Input {...props} size="xl" />;

interface ScheduleApplyFieldsProps {
    entityIds: number[];
    entitiesById: Map<number, Entity>;
    requiresSpecificDate: boolean;
    template: ScheduleTemplateResponse | undefined;
}

/** The invoice source's fields; a manual source uses `ScheduleManualBody`. */
export const ScheduleApplyFields: React.FC<ScheduleApplyFieldsProps> = ({
    entityIds,
    entitiesById,
    requiresSpecificDate,
    template,
}) => {
    const termStartLabelId = useId();
    const specificDateLabelId = useId();
    const { values, setValues, setFieldValue } =
        useFormikContext<ScheduleApplyFormValues>();
    const portalContainer = usePortalContainer();
    const firstUnlockedDate = getFirstUnlockedDateOnly(
        useLockDate(values.entityId),
    );
    const entityItems = useMemo<Array<PickerItemData<number>>>(
        () =>
            entityIds.map((id) => ({
                value: id,
                label: entitiesById.get(id)?.name ?? "Unknown entity",
            })),
        [entityIds, entitiesById],
    );

    const selectEntity = (entityId: number | null) => {
        void setValues((current) => ({
            ...current,
            entityId,
            invoiceId: null,
            sourceLineIds: [],
            amount: 0,
            termStart: "",
            sourceDate: "",
        }));
    };

    return (
        <VStack gap={16}>
            <VStack as="label" gap={6} className={styles.field}>
                <span className={styles.fieldLabel}>Entity</span>
                <Picker.Select
                    items={entityItems}
                    searchable
                    searchPlaceholder="Search entities..."
                    container={portalContainer}
                    value={values.entityId}
                    onValueChange={selectEntity}
                    placeholder="Select entity"
                    trigger={<Picker.Button variant="secondary" size="md" />}
                />
            </VStack>

            <ScheduleApplyInvoiceField
                container={portalContainer}
                template={template}
            />

            <VStack as="label" gap={6} className={styles.field}>
                <span className={styles.fieldLabel}>Amount</span>
                <NumberFormat
                    value={values.amount}
                    onValueChange={(numberValues) =>
                        void setFieldValue(
                            "amount",
                            numberValues.floatValue ?? 0,
                        )
                    }
                    prefix="$ "
                    decimalScale={2}
                    thousandSeparator
                    allowNegative={false}
                    customInput={ApplyFieldInput}
                />
            </VStack>

            <VStack as="label" gap={6} className={styles.field}>
                <span id={termStartLabelId} className={styles.fieldLabel}>
                    Term start
                </span>
                <fields.Date
                    name="termStart"
                    aria-labelledby={termStartLabelId}
                    showErrorMessage={false}
                    variant="panel-input"
                    minDate={firstUnlockedDate}
                />
            </VStack>

            {requiresSpecificDate && (
                <VStack as="label" gap={6} className={styles.field}>
                    <span
                        id={specificDateLabelId}
                        className={styles.fieldLabel}
                    >
                        Specific date
                    </span>
                    <fields.Date
                        name="specificDate"
                        aria-labelledby={specificDateLabelId}
                        showErrorMessage={false}
                        variant="panel-input"
                        minDate={firstUnlockedDate}
                    />
                </VStack>
            )}
        </VStack>
    );
};
